import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import DSModal from '../../../design-system/DSModal';
import DSBox from '../../../design-system/DSBox';
import DSSecondaryButton from '../../../design-system/DSSecondaryButton';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import { DSTypography } from '../../../design-system/Typography';
import { CalendarEvent } from '../../../api/calendarEvent';
import { isCalendarEventPast } from '../../../utils/calendarEvents';
import useCalendarEventsQuery from '../../Calendar/hooks/useCalendarEventsQuery';
import { useCountrySelection } from '../../Calendar/hooks/useCountrySelection';
import {
  KLYDO_YELLOW,
  KLYDO_WHITE,
  KLYDO_BLACK,
  TEXT_SECONDARY,
  TEXT_TERTIARY,
  TEXT_DISABLED,
} from '../../../consts/colors';
import { ReactComponent as GlobalIcon } from '../../../design-system/Icons/Global.svg';
import { ReactComponent as CollectionIcon } from '../../../design-system/Icons/CollectionIcon.svg';

/**
 * When a Feed klydo goes live once the review approves it.
 *
 * Renamed off `PUBLIC` / `CUSTOM_DATE`: "Public" is the word artists read as
 * "the marketplace", which is the exact confusion this modal exists to remove.
 */
export enum PublicationTiming {
  Automatic = 'Automatic',
  Scheduled = 'Scheduled',
}

/**
 * Where the artist wants this klydo to end up. The two are reviewed differently,
 * which is why the question is now asked before anything else:
 *
 *  - `Feed` — submitted on its own and reviewed on its own.
 *  - `Collection` — stays a draft and is reviewed with its Collection. Nothing is
 *    submitted now, so that branch calls no api at all.
 */
export enum PublicationDestination {
  Feed = 'Feed',
  Collection = 'Collection',
}

/** What the artist chose, handed to the caller on confirm. */
export type PublishSelection =
  | {
      destination: PublicationDestination.Feed;
      publicationTimestamp: number;
      isScheduled: boolean;
    }
  | { destination: PublicationDestination.Collection };

/**
 * Which publication action the caller will run when this dialog confirms. The
 * dialog itself neither reads nor renders this — the CALLER branches on it.
 *
 * `RequestPublication` used to be the other member, for a sealed klydo asking to
 * go public. The design removed that button from the sealed page, so opening a
 * review now happens only from the editor's own CTA, which never renders this.
 */
export enum PublishModalVariant {
  /** Request already approved — confirming only moves the go-live date. */
  SetPublicationDate = 'SetPublicationDate',
}

const DESTINATION_COPY = {
  title: 'Where should this Klydo go?',
  subtitle:
    'Feed Klydos are reviewed individually. Collection Klydos are reviewed with the whole Collection.',
};

const TIMING_ONLY_COPY = {
  title: 'When should this Klydo go live?',
  subtitle: 'Choose when it goes live — right away or on a specific date.',
};

const TIMING_COPY = {
  heading: 'When it goes live',
};

const TIMING_OPTIONS = [
  { value: PublicationTiming.Automatic, label: 'Submit automatically' },
  { value: PublicationTiming.Scheduled, label: 'Schedule for a specific date' },
];

const DESTINATIONS = [
  {
    value: PublicationDestination.Feed,
    label: 'Send to Feed',
    description: 'Daily content for all our users. Reviewed on its own.',
    Icon: GlobalIcon,
  },
  {
    value: PublicationDestination.Collection,
    label: 'Save for Collection',
    description:
      'Part of a set sold in the marketplace. Reviewed as a whole Collection.',
    Icon: CollectionIcon,
  },
];

/** One confirm verb for every branch of this dialog. */
const CONFIRM_LABEL = 'OK';

type PublishModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (selection: PublishSelection) => void;
  onPrevious?: () => void;
  defaultSelection?: PublicationTiming;
  prefilledTimestamp: number | null;
  /**
   * Ask Feed vs Collection before the date. Only a DRAFT has that choice — a
   * sealed klydo can no longer be put into a collection, so the read-only view
   * opens straight on the timing step and always confirms `Feed`.
   */
  askDestination?: boolean;
};

const calendarEventTimestamp = (event: CalendarEvent): number =>
  new Date(event.date.year, event.date.month - 1, event.date.day).getTime();

/**
 * Makes a `DSBox` behave like a button for keyboard users.
 *
 * The event rows, the month nav and the day cells are all divs carrying an
 * `onClick`. The MUI date picker this replaced was keyboard-navigable for free, so
 * without this the whole modal is mouse-only — a regression, not a missing nicety.
 */
const asButton = ({
  onActivate,
  label,
  selected,
  disabled,
}: {
  onActivate?: () => void;
  label?: string;
  selected?: boolean;
  disabled?: boolean;
}) => {
  const inert = disabled || !onActivate;
  return {
    role: 'button' as const,
    tabIndex: inert ? -1 : 0,
    'aria-label': label,
    'aria-pressed': selected,
    'aria-disabled': disabled || undefined,
    onClick: inert ? undefined : onActivate,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (inert) return;
      // Space and Enter both activate a native button, so both must here.
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate?.();
      }
    },
  };
};

/** Visible focus, since these are divs and get none by default. */
const FOCUS_RING_SX = {
  '&:focus-visible': {
    outline: `2px solid ${KLYDO_YELLOW}`,
    outlineOffset: 2,
  },
};

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * The pickable-row look shared by every choice in this modal — the two
 * destinations, the two timing options, each calendar event and the custom-date
 * row. Selection is a white hairline, NOT the brand yellow: the design keeps
 * yellow for the single confirm button and the chosen calendar day.
 */
const selectableRowSx = (isSelected: boolean, idleBorder = 'transparent') => ({
  ...FOCUS_RING_SX,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  border: `1.5px solid ${isSelected ? 'rgba(255, 255, 255, 0.5)' : idleBorder}`,
  background: isSelected
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(255, 255, 255, 0.03)',
  transition: 'background 0.2s ease',
  '&:hover': { background: 'rgba(255, 255, 255, 0.07)' },
});

/** The design's own radio: a ring with a dot faded in, not a MUI Radio. */
const RadioDot = ({ selected, size }: { selected: boolean; size: number }) => (
  <DSBox
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `2px solid ${selected ? KLYDO_WHITE : 'rgb(106, 106, 106)'}`,
    }}
  >
    <DSBox
      sx={{
        width: size - 10,
        height: size - 10,
        borderRadius: '50%',
        background: KLYDO_WHITE,
        opacity: selected ? 1 : 0,
      }}
    />
  </DSBox>
);

const PublishModal = ({
  open,
  onClose,
  onConfirm,
  onPrevious,
  defaultSelection = PublicationTiming.Automatic,
  prefilledTimestamp,
  askDestination = false,
}: PublishModalProps) => {
  const { spacing, palette } = useTheme();
  const headerCopy = askDestination ? DESTINATION_COPY : TIMING_ONLY_COPY;

  const { selectedCountry, selectedState } = useCountrySelection();
  const currentYear = new Date().getFullYear();
  const { data: calendarEvents, isLoading: isLoadingEvents } =
    useCalendarEventsQuery({
      country: selectedCountry,
      year: currentYear,
      state: selectedState,
      // The klydo views render this modal unconditionally, so without this the
      // events are fetched on every klydo page load.
      enabled: open,
    });

  const upcomingEvents = useMemo(
    () =>
      (calendarEvents ?? [])
        .filter((event) => !isCalendarEventPast(event))
        .sort((a, b) => calendarEventTimestamp(a) - calendarEventTimestamp(b)),
    [calendarEvents],
  );

  const [destination, setDestination] = useState<PublicationDestination | null>(
    null,
  );
  const [publicationTiming, setPublicationTiming] =
    useState<PublicationTiming>(defaultSelection);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  // `showCustomPicker` = the custom date is the chosen schedule (drives
  // validation + the check mark); `calendarOpen` = the inline month grid is
  // expanded (toggles independently, matching the design).
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    prefilledTimestamp ? dayjs(prefilledTimestamp) : dayjs().add(1, 'day'),
  );
  // First-of-month for the visible calendar page.
  const [calendarMonth, setCalendarMonth] = useState<Dayjs>(
    (prefilledTimestamp ? dayjs(prefilledTimestamp) : dayjs()).startOf('month'),
  );

  const didInitRef = useRef(false);

  useEffect(() => {
    if (!open) {
      didInitRef.current = false;
      return;
    }
    if (didInitRef.current) {
      return;
    }
    // Wait for events before initializing a prefilled schedule so we can try
    // to match it against a sponsored calendar event.
    if (
      defaultSelection === PublicationTiming.Scheduled &&
      prefilledTimestamp &&
      isLoadingEvents
    ) {
      return;
    }

    setDestination(null);
    setPublicationTiming(defaultSelection);

    if (
      defaultSelection === PublicationTiming.Scheduled &&
      prefilledTimestamp
    ) {
      const matchedEvent = upcomingEvents.find(
        (event) => calendarEventTimestamp(event) === prefilledTimestamp,
      );
      if (matchedEvent) {
        setSelectedEventId(matchedEvent.id);
        setShowCustomPicker(false);
        setCalendarOpen(false);
        setSelectedDate(dayjs(prefilledTimestamp));
      } else {
        setSelectedEventId(null);
        setShowCustomPicker(true);
        setCalendarOpen(true);
        setSelectedDate(dayjs(prefilledTimestamp));
      }
      setCalendarMonth(dayjs(prefilledTimestamp).startOf('month'));
    } else {
      setSelectedEventId(null);
      setShowCustomPicker(false);
      setCalendarOpen(false);
      const initialDate = prefilledTimestamp
        ? dayjs(prefilledTimestamp)
        : dayjs().add(1, 'day');
      setSelectedDate(initialDate);
      setCalendarMonth(initialDate.startOf('month'));
    }

    didInitRef.current = true;
  }, [
    open,
    defaultSelection,
    prefilledTimestamp,
    isLoadingEvents,
    upcomingEvents,
  ]);

  // The timing step belongs to the Feed branch only: a Collection klydo is not
  // being submitted now, so it has no go-live date to pick.
  const showTiming =
    !askDestination || destination === PublicationDestination.Feed;

  // Resolved, not just remembered: the events list is refetched (and re-filtered
  // for past dates and the selected country/state) while the modal is open, so a
  // remembered id can stop matching anything. Confirming on a stale id used to
  // fall through to `selectedDate` — the untouched default of tomorrow — and
  // silently schedule the wrong day.
  const selectedEvent = useMemo(
    () =>
      selectedEventId
        ? upcomingEvents.find((event) => event.id === selectedEventId)
        : undefined,
    [selectedEventId, upcomingEvents],
  );

  const isScheduleValid =
    !!selectedEvent || (showCustomPicker && !!selectedDate);
  const isSubmitDisabled =
    (askDestination && destination === null) ||
    (showTiming &&
      publicationTiming === PublicationTiming.Scheduled &&
      !isScheduleValid);

  const handleSelectEvent = (eventId: string) => {
    setSelectedEventId(eventId);
    setShowCustomPicker(false);
    setCalendarOpen(false);
  };

  const handleOpenCustomDate = () => {
    const base = selectedDate ?? dayjs().add(1, 'day');
    setSelectedEventId(null);
    setShowCustomPicker(true);
    if (!selectedDate) {
      setSelectedDate(base);
    }
    setCalendarMonth(base.startOf('month'));
    // Re-clicking the row collapses/expands the grid (matches the design).
    setCalendarOpen((prev) => !prev);
  };

  const handleSelectDay = (date: Dayjs) => {
    setSelectedEventId(null);
    setShowCustomPicker(true);
    setSelectedDate(date);
  };

  const handleConfirm = () => {
    if (destination === PublicationDestination.Collection) {
      onConfirm({ destination: PublicationDestination.Collection });
      return;
    }

    const confirmFeed = (publicationTimestamp: number, isScheduled: boolean) =>
      onConfirm({
        destination: PublicationDestination.Feed,
        publicationTimestamp,
        isScheduled,
      });

    if (publicationTiming === PublicationTiming.Automatic) {
      confirmFeed(Date.now(), false);
      return;
    }

    if (selectedEvent) {
      confirmFeed(calendarEventTimestamp(selectedEvent), true);
      return;
    }

    if (selectedDate) {
      confirmFeed(selectedDate.valueOf(), true);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const shouldDisableDate = (date: Dayjs) => date.isBefore(dayjs(), 'day');

  // Leading blanks + each day of the visible month, laid out on a 7-col grid.
  const calendarCells = useMemo<(Dayjs | null)[]>(() => {
    const monthStart = calendarMonth.startOf('month');
    const leadingBlanks = monthStart.day();
    const daysInMonth = calendarMonth.daysInMonth();
    const cells: (Dayjs | null)[] = Array.from(
      { length: leadingBlanks },
      () => null,
    );
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(monthStart.date(day));
    }
    return cells;
  }, [calendarMonth]);

  const navButtonSx = {
    width: 28,
    height: 28,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: KLYDO_WHITE,
    cursor: 'pointer',
    background: 'rgba(255, 255, 255, 0.08)',
    transition: 'background 0.15s ease',
    '&:hover': { background: 'rgba(255, 255, 255, 0.16)' },
  };

  return (
    <DSModal open={open} onClose={handleClose}>
      <DSBox
        sx={{
          width: { xs: '90vw', sm: askDestination ? 560 : 440 },
          padding: '30px 34px 28px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <DSBox sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <DSBox sx={{ flex: 1, minWidth: 0 }}>
            <DSTypography
              sx={{ fontSize: 23, fontWeight: 700, lineHeight: 1.2 }}
            >
              {headerCopy.title}
            </DSTypography>
            <DSTypography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.5,
                color: TEXT_SECONDARY,
                marginTop: '8px',
              }}
            >
              {headerCopy.subtitle}
            </DSTypography>
          </DSBox>
          <DSBox
            {...asButton({ onActivate: handleClose, label: 'Close' })}
            sx={{
              ...FOCUS_RING_SX,
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: KLYDO_WHITE,
              background: palette.paper.elevation6,
              transition: 'background 0.15s ease',
              '&:hover': { background: 'rgba(255, 255, 255, 0.16)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </DSBox>
        </DSBox>

        {askDestination && (
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginTop: '24px',
            }}
          >
            {DESTINATIONS.map(({ value, label, description, Icon }) => {
              const isSelected = destination === value;
              return (
                <DSBox
                  key={value}
                  {...asButton({
                    onActivate: () => setDestination(value),
                    label,
                    selected: isSelected,
                  })}
                  sx={{
                    ...selectableRowSx(isSelected, 'rgba(255, 255, 255, 0.08)'),
                    gap: '14px',
                    padding: '16px',
                    borderRadius: '14px',
                  }}
                >
                  <RadioDot selected={isSelected} size={20} />
                  <DSBox
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: KLYDO_WHITE,
                      background: 'rgba(255, 255, 255, 0.06)',
                      '& svg': { width: 20, height: 20 },
                    }}
                  >
                    <Icon />
                  </DSBox>
                  <DSBox sx={{ flex: 1, minWidth: 0 }}>
                    <DSTypography
                      sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {label}
                    </DSTypography>
                    <DSTypography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        lineHeight: 1.4,
                        color: TEXT_SECONDARY,
                        marginTop: '4px',
                      }}
                    >
                      {description}
                    </DSTypography>
                  </DSBox>
                </DSBox>
              );
            })}
          </DSBox>
        )}

        {showTiming && (
          <DSBox
            sx={{
              marginTop: '16px',
              padding: '16px',
              borderRadius: '16px',
              background: 'rgba(0, 0, 0, 0.22)',
            }}
          >
            <DSTypography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1,
                marginBottom: '12px',
              }}
            >
              {TIMING_COPY.heading}
            </DSTypography>

            <DSBox
              sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
            >
              {TIMING_OPTIONS.map(({ value, label }) => {
                const isSelected = publicationTiming === value;
                return (
                  <DSBox
                    key={value}
                    {...asButton({
                      onActivate: () => setPublicationTiming(value),
                      label,
                      selected: isSelected,
                    })}
                    sx={{
                      ...selectableRowSx(isSelected),
                      gap: '12px',
                      padding: '13px 14px',
                      borderRadius: '12px',
                    }}
                  >
                    <RadioDot selected={isSelected} size={19} />
                    <DSTypography
                      sx={{
                        flex: 1,
                        fontSize: 15,
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    >
                      {label}
                    </DSTypography>
                  </DSBox>
                );
              })}
            </DSBox>

            {publicationTiming === PublicationTiming.Scheduled && (
              <>
                <DSBox
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    maxHeight: 172,
                    overflowY: 'auto',
                    paddingRight: '4px',
                    margin: '14px 0 0 31px',
                  }}
                >
                  {upcomingEvents.map((event) => {
                    const eventDate = new Date(
                      event.date.year,
                      event.date.month - 1,
                      event.date.day,
                    );
                    const chip = dayjs(eventDate).format('MMM D').toUpperCase();
                    const weekday = dayjs(eventDate).format('dddd');
                    const isSelected = selectedEventId === event.id;

                    return (
                      <DSBox
                        key={event.id}
                        {...asButton({
                          onActivate: () => handleSelectEvent(event.id),
                          label: `${event.name}, ${weekday} ${chip}`,
                          selected: isSelected,
                        })}
                        sx={{
                          ...selectableRowSx(isSelected),
                          gap: '14px',
                          padding: '10px 12px',
                          borderRadius: '12px',
                        }}
                      >
                        <DSTypography
                          sx={{
                            flexShrink: 0,
                            minWidth: 64,
                            textAlign: 'center',
                            padding: '7px 10px',
                            borderRadius: '8px',
                            background: 'rgba(255, 255, 255, 0.07)',
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          {chip}
                        </DSTypography>
                        <DSTypography
                          sx={{
                            width: 78,
                            flexShrink: 0,
                            fontSize: 13,
                            fontWeight: 500,
                            lineHeight: 1,
                            color: TEXT_SECONDARY,
                          }}
                        >
                          {weekday}
                        </DSTypography>
                        <DSTypography
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 14,
                            fontWeight: 600,
                            lineHeight: 1.2,
                          }}
                        >
                          {event.name}
                          {event.emojis?.length
                            ? ` ${event.emojis.join(' ')}`
                            : ''}
                        </DSTypography>
                        <CheckIcon
                          sx={{
                            flexShrink: 0,
                            fontSize: 17,
                            color: KLYDO_WHITE,
                            opacity: isSelected ? 1 : 0,
                          }}
                        />
                      </DSBox>
                    );
                  })}

                  {!isLoadingEvents && upcomingEvents.length === 0 && (
                    <DSTypography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: TEXT_SECONDARY,
                        padding: '4px 8px',
                      }}
                    >
                      No upcoming events available.
                    </DSTypography>
                  )}
                </DSBox>

                <DSBox
                  {...asButton({
                    onActivate: handleOpenCustomDate,
                    label: 'Pick a custom date',
                    selected: showCustomPicker,
                  })}
                  sx={{
                    ...selectableRowSx(showCustomPicker),
                    gap: '14px',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    marginTop: '8px',
                  }}
                >
                  <DSBox
                    sx={{
                      width: 38,
                      height: 30,
                      flexShrink: 0,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: KLYDO_WHITE,
                      background: 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <CalendarTodayIcon sx={{ fontSize: 16 }} />
                  </DSBox>
                  <DSTypography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {showCustomPicker && selectedDate
                      ? selectedDate.format('MM.DD.YY')
                      : 'Pick a custom date…'}
                  </DSTypography>
                  <CheckIcon
                    sx={{
                      flexShrink: 0,
                      fontSize: 17,
                      color: KLYDO_WHITE,
                      opacity: showCustomPicker ? 1 : 0,
                    }}
                  />
                </DSBox>

                {calendarOpen && (
                  <DSBox
                    sx={{
                      marginTop: '8px',
                      padding: '14px',
                      borderRadius: '14px',
                      background: 'rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <DSBox
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px',
                      }}
                    >
                      <DSBox
                        {...asButton({
                          onActivate: () =>
                            setCalendarMonth((month) =>
                              month.subtract(1, 'month'),
                            ),
                          label: 'Previous month',
                        })}
                        sx={{ ...navButtonSx, ...FOCUS_RING_SX }}
                      >
                        <ChevronLeftIcon sx={{ fontSize: 14 }} />
                      </DSBox>
                      <DSTypography
                        sx={{
                          flex: 1,
                          textAlign: 'center',
                          fontSize: 14,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {calendarMonth.format('MMMM YYYY')}
                      </DSTypography>
                      <DSBox
                        {...asButton({
                          onActivate: () =>
                            setCalendarMonth((month) => month.add(1, 'month')),
                          label: 'Next month',
                        })}
                        sx={{ ...navButtonSx, ...FOCUS_RING_SX }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 14 }} />
                      </DSBox>
                    </DSBox>

                    <DSBox
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                        marginBottom: '6px',
                      }}
                    >
                      {WEEKDAY_LABELS.map((label, index) => (
                        <DSTypography
                          key={`${label}-${index}`}
                          sx={{
                            textAlign: 'center',
                            fontSize: 10,
                            fontWeight: 600,
                            lineHeight: 1,
                            color: TEXT_TERTIARY,
                          }}
                        >
                          {label}
                        </DSTypography>
                      ))}
                    </DSBox>

                    <DSBox
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                      }}
                    >
                      {calendarCells.map((date, index) => {
                        if (!date) {
                          return (
                            <DSBox key={`blank-${index}`} sx={{ height: 32 }} />
                          );
                        }
                        const isDaySelected =
                          !!selectedDate && selectedDate.isSame(date, 'day');
                        const isDayDisabled = shouldDisableDate(date);
                        return (
                          <DSBox
                            key={date.toISOString()}
                            {...asButton({
                              onActivate: () => handleSelectDay(date),
                              label: date.format('D MMMM YYYY'),
                              selected: isDaySelected,
                              disabled: isDayDisabled,
                            })}
                            sx={{
                              ...FOCUS_RING_SX,
                              height: 32,
                              borderRadius: '9px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 13,
                              fontWeight: isDaySelected ? 700 : 500,
                              cursor: isDayDisabled ? 'default' : 'pointer',
                              color: isDaySelected
                                ? KLYDO_BLACK
                                : isDayDisabled
                                  ? TEXT_DISABLED
                                  : KLYDO_WHITE,
                              background: isDaySelected
                                ? KLYDO_YELLOW
                                : 'rgba(255, 255, 255, 0.05)',
                              transition: 'background 0.15s ease',
                              '&:hover':
                                isDayDisabled || isDaySelected
                                  ? undefined
                                  : { background: 'rgba(255, 255, 255, 0.12)' },
                            }}
                          >
                            {date.date()}
                          </DSBox>
                        );
                      })}
                    </DSBox>
                  </DSBox>
                )}
              </>
            )}
          </DSBox>
        )}

        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: onPrevious ? 'space-between' : 'flex-end',
            gap: '12px',
            marginTop: '22px',
          }}
        >
          {onPrevious && (
            <DSSecondaryButton
              onClick={onPrevious}
              sx={{
                textDecoration: 'underline',
                paddingX: spacing(2),
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Previous
            </DSSecondaryButton>
          )}
          <DSBox sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <DSSecondaryButton
              onClick={handleClose}
              sx={{
                paddingX: spacing(2),
              }}
            >
              Cancel
            </DSSecondaryButton>
            <DSPrimaryButton
              onClick={handleConfirm}
              disabled={isSubmitDisabled}
            >
              {CONFIRM_LABEL}
            </DSPrimaryButton>
          </DSBox>
        </DSBox>
      </DSBox>
    </DSModal>
  );
};

export default PublishModal;
